import { useTranslation } from 'react-i18next';
import { Badge } from '@figma/fpl-components';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { FlexCenVer } from '@/UI/styles';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { PermissionsContainer } from '@/components/PermissionsContainer/PermissionsContainer';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useCreditsBalanceDisplay } from '@/hooks/useCreditsBalanceDisplay';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

export interface WorkflowCreditsDisplayProps {
  showCreditsLabel?: boolean;
}

export function WorkflowCreditsDisplay({ showCreditsLabel = false }: WorkflowCreditsDisplayProps) {
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { creditsDisplayText, isLowCredits, handleGetMoreCreditsClick } = useCreditsBalanceDisplay();
  const { isAllowedForPermission } = useSubscriptionPermissions();
  const { t } = useTranslation();
  const isCreditsActionAllowed = isAllowedForPermission('credits', 'action');

  // Credits balance + the "get more credits" CTA are Weavy-billing surfaces. In
  // Figma embed mode billing is owned by Figma's surrounding chrome — surface
  // them in the Figma UI, not inside the embedded editor.
  if (isFigmaEmbedMode) return null;

  return (
    <PermissionsContainer permission="credits">
      <Tooltip
        enterDelay={800}
        enterNextDelay={800}
        title={t(I18N_KEYS.FLOW_NAVBAR.GET_MORE_CREDITS_V2)}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -10],
                },
              },
            ],
          },
        }}
      >
        <FlexCenVer
          sx={{
            height: 24,
            gap: 0.25,
            cursor: 'pointer',
            pointerEvents: isCreditsActionAllowed ? 'auto' : 'none',
            px: 0.5,
            borderRadius: 1,
            '&:hover': { bgcolor: isFplMigrationEnabled ? fplTokens.color.bgTransparentHover : color.Black84 },
          }}
          onClick={handleGetMoreCreditsClick}
        >
          <AsteriskIcon width={16} height={16} />
          {isLowCredits ? (
            <FlexCenVer sx={{ gap: 0.75 }}>
              <TextSmRg>{creditsDisplayText}</TextSmRg>
              <Badge variant="warningOutline" size="md">
                <span style={{ fontWeight: 'normal' }}>{t(I18N_KEYS.FLOW_NAVBAR.LOW_CREDITS_LABEL)}</span>
              </Badge>
            </FlexCenVer>
          ) : (
            <TextSmRg>
              {showCreditsLabel
                ? t(I18N_KEYS.GENERAL.CREDITS_DISPLAY, { credits: creditsDisplayText })
                : creditsDisplayText}
            </TextSmRg>
          )}
        </FlexCenVer>
      </Tooltip>
    </PermissionsContainer>
  );
}
